// @flow

import React, {Fragment} from 'react';
import gql from 'graphql-tag';
import {FormattedMessage} from 'react-intl';
import PriceSummary from '@homeaway/react-price-summary';
import {currencyFormatter} from '@homeaway/hacommons-i18n';
import InstantBookIcon from '../InstantBookIcon/InstantBookIcon';
import {getListingPrice} from '../../utils/priceUtils';
import type {IntlShape} from 'react-intl';
import type {ListingShape} from '../Hit/Hit';
import {type MultiUnitItemShape} from '../MultiUnitItem/MultiUnitItem';
import './Price.less';

export const fragments = {
    listing: gql`
        fragment PriceListing on Listing {
            averagePrice {
                currencyUnits
                periodType
                value
            }
            totalPrice {
                currencyUnits
                periodType
                value
            }
            priceSummary {
                edapEventJson
                formattedAmount
                pricePeriodDescription
            }
        }
    `
};

export type PriceShape = {
    currencyUnits: string,
    periodType: string,
    value: number
};

export type PriceSummaryShape = {
    edapEventJson: string,
    formattedAmount: string,
    pricePeriodDescription: string
};

export type PriceListingShape = {
    priceSummary: ?PriceSummaryShape,
    averagePrice: ?PriceShape,
    totalPrice: ?PriceShape
};

export type Props = {
    listing: ListingShape | MultiUnitItemShape,
    useAverageLabel?: boolean,
    useShortLabel?: boolean,
    fullLocale: string
};

type WithInstantBookProps = {
    intl: IntlShape,
    instantBookable?: boolean
} & Props;

const TOTAL_PERIOD_TYPE = 'TOTAL';

const PeriodFormatted = ({useAverageLabel, useShortLabel, useTotalLabel}) => {
    let id;
    let defaultMessage;

    if (useShortLabel) {
        id = 'ratesNightlyShort';
        defaultMessage = '/ night';
    } else {
        id = useAverageLabel ? 'ratesAverage' : 'ratesNightly';
        defaultMessage = useAverageLabel ? 'avg/night' : 'per night';
    }

    if (useTotalLabel) {
        id = 'ratesTotal';
        defaultMessage = 'Total price';
    }

    return (
        <FormattedMessage
            id={id}
            description={'price period'}
            defaultMessage={defaultMessage}
        >
            {(txt) => <span className="Price__Fallback">{txt}</span>}
        </FormattedMessage>
    );
};

/* the following node-hacommons-i18n can directly produce the correct currency format and symbol given
the locale and the currency (e.g., 'USD').  */
export function Price({
    listing,
    useAverageLabel,
    useShortLabel,
    fullLocale
}: Props) {
    if (listing.priceSummary) {
        return <PriceSummary priceSummary={listing.priceSummary} />;
    }
    const {currencyUnits, value, periodType} = getListingPrice(listing) || {};

    if (currencyUnits && value) {
        const useTotalLabel = periodType === TOTAL_PERIOD_TYPE;

        return (
            <Fragment>
                <span
                    className="Price__value"
                    data-wdio="Price"
                    data-price={value}
                >
                    {`${currencyFormatter.formatNumber({
                        value,
                        currencyCode: currencyUnits,
                        locale: fullLocale
                    })} `}
                </span>
                <PeriodFormatted
                    useAverageLabel={useAverageLabel}
                    useTotalLabel={useTotalLabel}
                    useShortLabel={useShortLabel}
                />
            </Fragment>
        );
    }

    return (
        <span className="Price Price--noPrice" data-wdio="Price" data-price="0">
            <FormattedMessage
                id={'ratesNone'}
                description={'no price'}
                defaultMessage={'View details for price'}
            >
                {(txt) => <span className="Price__Fallback">{txt}</span>}
            </FormattedMessage>
        </span>
    );
}

export default function PriceWithInstantBook(props: WithInstantBookProps) {
    return (
        <Fragment>
            {props.instantBookable && <InstantBookIcon intl={props.intl} />}
            <Price {...props} />
        </Fragment>
    );
}
